import * as React from 'react'
import { useEffect, useMemo, useRef } from 'react'
import { useUsersList } from './hooks'
import { path } from '../../../../libs'
import { Button, Divider, InputRef, Space, Table } from 'antd'
import { EyeOutlined } from '@ant-design/icons'
import { User } from './types'
import { ColumnsType } from 'antd/lib/table'
import { getColumnSearchProps } from '../../../../libs/tableFunctions'
import { changedDateFormat } from '../../../../libs/utils'
import { useNavigate } from 'react-router-dom'
import { deleteUserMounted, restoreUserMounted } from './model'
import { Guard } from '../../../../features/session/guard'
import { useUrlFilters } from '../../../../libs/useUrlFilters'

const FILTER_CONFIG = {
  id: { type: 'string' as const },
  username: { type: 'string' as const },
  email: { type: 'string' as const },
  firstName: { type: 'string' as const },
  lastName: { type: 'string' as const },
  page: { type: 'string' as const, default: '1', pagination: true },
  pageSize: { type: 'string' as const, default: '10', pagination: true },
  sortField: { type: 'string' as const, default: 'email', pagination: true },
  sortOrder: {
    type: 'string' as const,
    default: 'ascend',
    pagination: true,
  },
}

const SEARCH_KEYS = ['id', 'username', 'email', 'firstName', 'lastName'] as const

export const UsersList = () => {
  const navigate = useNavigate()

  const { users, loading, onChangeUsersList, totalCountOfUsers } =
    useUsersList()

  const { filters, setFilters } = useUrlFilters(FILTER_CONFIG)

  const page = Number(filters.page) || 1
  const pageSize = Number(filters.pageSize) || 10
  const orderBy = filters.sortField || 'email'
  const orderDirection = filters.sortOrder === 'descend' ? '-' : ''

  const searchText = useMemo(() => {
    const result: Record<string, string> = {}
    for (const key of SEARCH_KEYS) {
      const value = filters[key]
      if (value) result[key] = value
    }
    return result
  }, [filters])

  const searchInput = useRef<InputRef>(null)

  const setSearchText: React.Dispatch<
    React.SetStateAction<{ [key: string]: string }>
  > = (updater) => {
    const next =
      typeof updater === 'function' ? updater({ ...searchText }) : updater
    const updates: Record<string, any> = { page: undefined }
    for (const key of SEARCH_KEYS) {
      updates[key] = next[key] || undefined
    }
    setFilters(updates as any)
  }

  useEffect(() => {
    onChangeUsersList({
      limit: pageSize,
      page,
      orderBy,
      orderDirection,
      searchText,
    })
  }, [pageSize, page, orderBy, orderDirection, searchText])

  const columns: ColumnsType<User> = [
    {
      title: 'ID',
      dataIndex: 'id',
      ...getColumnSearchProps<User>(
        'id',
        setSearchText,
        searchText,
        searchInput,
      ),
      sorter: (a, b) => a.email.length - b.email.length,
    },
    {
      title: 'Username',
      dataIndex: 'username',
      ...getColumnSearchProps<User>(
        'username',
        setSearchText,
        searchText,
        searchInput,
      ),
      sorter: (a, b) => a.email.length - b.email.length,
    },
    {
      title: 'E-mail',
      dataIndex: 'email',
      ...getColumnSearchProps<User>(
        'email',
        setSearchText,
        searchText,
        searchInput,
      ),
      sorter: (a, b) => a.email.length - b.email.length,
    },
    {
      title: 'First name',
      dataIndex: 'firstName',
      ...getColumnSearchProps<User>(
        'firstName',
        setSearchText,
        searchText,
        searchInput,
      ),
    },
    {
      title: 'Last name',
      dataIndex: 'lastName',
      ...getColumnSearchProps<User>(
        'lastName',
        setSearchText,
        searchText,
        searchInput,
      ),
    },
    {
      title: 'Position',
      dataIndex: 'position',
    },
    {
      title: 'Role',
      render: (item: User) =>
        item.userGroups.map((group) => <div key={group.id}>{group.name}</div>),
    },
    {
      title: 'Date of registration',
      render: (item: User) =>
        item.userGroups.map((group) => (
          <div key={group.id}>{changedDateFormat(new Date(group.createdAt))}</div>
        )),
    },
    {
      title: 'Actions',
      fixed: 'right',
      width: 150,
      render: (item: User) => (
        <Button
          icon={<EyeOutlined />}
          onClick={() => navigate(path.users.details(item.id))}
          title="View"
        />
      ),
    },
    {
      title: 'Block / Unblock',
      fixed: 'right',
      width: 150,
      render: (item: User) => (
        <Button
          danger={!item.deletedAt}
          type={item.deletedAt ? 'primary' : 'default'}
          onClick={() => {
            if (window.confirm('Are you sure?')) {
              Boolean(item.deletedAt)
                ? restoreUserMounted({ id: item.id })
                : deleteUserMounted({ id: item.id })
            }
          }}
        >
          {item.deletedAt ? 'Unblock' : 'Block'}
        </Button>
      ),
    },
  ]

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Guard permission="edit_users">
        <Button
          type="primary"
          onClick={() => navigate(path.users.create())}
        >
          Create user
        </Button>
      </Guard>
      <Divider style={{ margin: 0 }} />
      <Table
        dataSource={users}
        columns={columns}
        bordered={true}
        scroll={{ x: true }}
        loading={loading}
        pagination={{
          current: page,
          pageSize,
          showSizeChanger: true,
          total: totalCountOfUsers,
        }}
        rowKey="id"
        onChange={(pagination, _tableFilters, sorter) => {
          const updates: Record<string, any> = {}
          if (pagination.current) {
            updates.page =
              pagination.current === 1 ? undefined : String(pagination.current)
          }
          if (pagination.pageSize && pagination.pageSize !== pageSize) {
            updates.pageSize =
              pagination.pageSize === 10
                ? undefined
                : String(pagination.pageSize)
            updates.page = undefined
          }
          // @ts-ignore
          const field = sorter.field || sorter.columnKey
          // @ts-ignore
          const order = sorter.order as 'ascend' | 'descend' | undefined
          if (field) {
            updates.sortField = field === 'email' ? undefined : String(field)
            updates.sortOrder =
              order === 'descend'
                ? 'descend'
                : order === 'ascend'
                ? undefined
                : undefined
          }
          setFilters(updates as any)
        }}
      />
    </Space>
  )
}
